import React, { useState } from "react";
import { Order, OrderItem } from "../../../types/orderTypes";
import { ElectricBolt, ExpandMore, ExpandLess } from "@mui/icons-material";
import { Avatar, Button, Chip, CircularProgress } from "@mui/material";
import { teal } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../State/Store";
import { cancelOrder } from "../../../State/customer/orderSlice";
import OrderStepper from "./OrderStepper";

interface OrderItemCardProps {
    item: OrderItem;
    order: Order;
}

const OrderItemCard: React.FC<OrderItemCardProps> = ({ item, order }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [showTracking, setShowTracking] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const imageUrl = item.product?.images?.[0] || "";
    const sellerName = item.product?.seller?.businessDetails?.businessName || "Vendor";

    const normalizedStatus = order.orderStatus?.toUpperCase().trim() || "PENDING";
    const isCancellable = ["PENDING", "PLACED", "CONFIRMED"].includes(normalizedStatus);

    // Color mapper for status badge
    const getStatusChip = (status: string) => {
        switch (status) {
            case "PENDING":
                return <Chip label="PENDING" size="small" color="warning" variant="outlined" />;
            case "PLACED":
            case "CONFIRMED":
                return <Chip label={status} size="small" color="info" variant="outlined" />;
            case "SHIPPED":
                return <Chip label="SHIPPED" size="small" color="primary" variant="outlined" />;
            case "DELIVERED":
                return <Chip label="DELIVERED" size="small" color="success" />;
            case "CANCELLED":
                return <Chip label="CANCELLED" size="small" color="error" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const handleCancelOrder = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to cancel this order?")) {
            const jwt = localStorage.getItem("jwt") || "";
            if (jwt && order.id) {
                setIsCancelling(true);
                await dispatch(cancelOrder({ orderId: order.id, jwt }));
                setIsCancelling(false);
            }
        }
    };

    return (
        <div className="bg-white p-5 border rounded-md shadow-sm space-y-4">
            {/* Header: Status & Actions */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <div className="flex items-center gap-3">
                    <Avatar sizes="small" sx={{ bgcolor: teal[500] }}>
                        <ElectricBolt />
                    </Avatar>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-bold text-gray-800">Order #{order.id}</h1>
                            {getStatusChip(normalizedStatus)}
                        </div>
                        <p className="text-xs text-gray-500">
                            {normalizedStatus === "DELIVERED"
                                ? `Delivered on ${order.deliveredDate || "N/A"}`
                                : `Arriving by ${order.deliveredDate || "N/A"}`}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {isCancellable && (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={isCancelling}
                            onClick={handleCancelOrder}
                        >
                            {isCancelling ? <CircularProgress size={18} color="inherit" /> : "Cancel Order"}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowTracking(!showTracking)}
                        endIcon={showTracking ? <ExpandLess /> : <ExpandMore />}
                    >
                        {showTracking ? "Hide Tracking" : "Track Order"}
                    </Button>
                </div>
            </div>

            {/* Product Details Card (Navigates to detail view) */}
            <div
                onClick={() => navigate(`/account/order/${order.id}/${item.id}`)}
                className="p-4 bg-teal-50/50 hover:bg-teal-50 rounded-md flex gap-4 cursor-pointer transition-colors"
            >
                {imageUrl && (
                    <img
                        className="w-20 h-20 object-cover rounded border bg-white"
                        src={imageUrl}
                        alt={item.product?.title || "Product"}
                    />
                )}
                <div className="w-full space-y-1">
                    <span className="text-xs font-semibold text-teal-700 uppercase tracking-wider">{sellerName}</span>
                    <h2 className="font-semibold text-gray-900 line-clamp-1">{item.product?.title}</h2>
                    <p className="text-xs text-gray-600"><strong>Quantity:</strong> {item.quantity || 1}</p>
                    <p className="text-xs text-gray-500"><strong>Order Date:</strong> {order.orderDate}</p>

                    <p className="text-xs text-gray-500">
                        <strong>M-Pesa Receipt:</strong>{" "}
                        {order.paymentDetails?.mpesaReceiptNumber || "Not available"}
                    </p>

                </div>
            </div>

            {/* Collapsible Order Tracking Stepper */}
            {showTracking && (
                <div className="pt-2 border-t">
                    <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide">Order Progress</h3>
                    <OrderStepper orderStatus={order.orderStatus} />
                </div>
            )}
        </div>
    );
};

export default OrderItemCard;